/**
 * Copyright (c) 2021-2024 Kognitos, Inc. All rights reserved.
 */
import { gql } from '@apollo/client';
import {
  CORE_PROCEDURE_DATA_FRAGMENT,
  PROCEDURE_SUMMARY_FRAGMENT
} from '../fragments/process';

export const PARSE_ALANG_TEXT = gql`
  query ParseAlangText(
    $statement: String!
    $scopeCode: String!
    $language: String!
    $departmentId: ID!
    $version: String
  ) {
    parseAlangText(
      statement: $statement
      scopeCode: $scopeCode
      language: $language
      departmentId: $departmentId
      version: $version
    ) {
      ast
      text
      error
    }
  }
`;

export const GET_PROCEDURE = gql(`
  query GetProcedure(
    $procedureId: ID!
    $knowledgeId: ID
    $departmentId: ID
    $version: String
    $procedureStage: ProcedureStage
  ) {
    getProcedure(
      procedureId: $procedureId
      knowledgeId: $knowledgeId
      departmentId: $departmentId
      version: $version
      procedureStage: $procedureStage
    ) {
      id
      knowledgeId
      departmentId
      text
      name
      owner
      language
      bookReference
      stage
      schedules {
        name
        expression
        enabled
      }
      email {
        stage
        email
      }
      title
      version
      assignmentPolicy {
        policyType
        collaborator
      }
    }
  }
`);

export const LIST_PROCEDURES_BY_DEPARTMENT = gql`
  query ListProceduresByDepartment(
    $departmentId: ID!
    $limit: Int = 100
    $version: String
    $nextToken: String
    $procedureFilter: ListProceduresFilter
  ) {
    listProceduresByDepartment(
      departmentId: $departmentId
      limit: $limit
      version: $version
      nextToken: $nextToken
      procedureFilter: $procedureFilter
    ) {
      items {
        id
        knowledgeId
        departmentId
        name
        owner
        language
        public
        bookReference
        title
        version
      }
      nextToken
    }
  }
`;

export const LIST_BASIC_PROCEDURES_BY_DEPARTMENT_ID = gql`
  query ListBasicWorkersByProcedure(
    $procedureId: ID
    $limit: Int = 1000
    $nextToken: String
    $stateFilter: String
  ) {
    listWorkersByProcedure(
      procedureId: $procedureId
      limit: $limit
      nextToken: $nextToken
      stateFilter: $stateFilter
    ) {
      items {
        id
        name
        createdAt
        originalWorkerId
        state
        stage
      }
      nextToken
    }
  }
`;

export const LIST_WORKERS_BY_PROCEDURE_ID = gql`
  query ListWorkersByProcedure(
    $procedureId: ID
    $limit: Int = 1000
    $nextToken: String
    $stateFilter: String
    $fromCreationDate: String
    $toCreationDate: String
    $stage: ProcedureStage
  ) {
    listWorkersByProcedure(
      procedureId: $procedureId
      limit: $limit
      nextToken: $nextToken
      stateFilter: $stateFilter
      fromCreationDate: $fromCreationDate
      toCreationDate: $toCreationDate
      stage: $stage
    ) {
      items {
        id
        owner
        name
        state
        knowledgeId
        departmentId
        procedureId
        createdAt
        originalWorkerId
        state
        stateLastUpdatedAt
        attachments
        replayWorkerId
      }
      nextToken
    }
  }
`;

export const LIST_WORKER_OUTPUTS_BY_PROCEDURE = gql`
  query ListWorkerOutputsByProcedure(
    $procedureId: ID!
    $knowledgeId: ID
    $page: Int
    $pageSize: Int = 10
    $decodeConcepts: Boolean
    $departmentId: ID
    $stateFilter: String
    $fromDate: String
    $toDate: String
    $limit: Int = 10
    $nextToken: String
    $stage: ProcedureStage
  ) {
    listWorkerOutputsByProcedure(
      procedureId: $procedureId
      knowledgeId: $knowledgeId
      page: $page
      pageSize: $pageSize
      decodeConcepts: $decodeConcepts
      departmentId: $departmentId
      stateFilter: $stateFilter
      fromDate: $fromDate
      toDate: $toDate
      limit: $limit
      nextToken: $nextToken
      stage: $stage
    ) {
      columns
      data
      nextToken
    }
  }
`;

export const LIST_SHARED_ENTITY_PROCEDURE = gql`
  query ListSharedEntityByProcedure(
    $departmentId: ID!
    $procedureId: ID!
    $version: String
    $limit: Int = 50
    $nextToken: String
  ) {
    listSharedEntityByProcedure(
      departmentId: $departmentId
      procedureId: $procedureId
      version: $version
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        entityId
        departmentId
        shareName
        fullShareId
        departmentVersion
      }
      nextToken
    }
  }
`;

export const EXPORT_CODE_FOR_CONTEXT = gql`
  query ExportCodeForContext($workerId: ID!, $contextId: ID) {
    exportCodeForContext(workerId: $workerId, contextId: $contextId)
  }
`;

export const LIST_PROCEDURE_GROUPS_BY_DEPARTMENT = gql`
  ${CORE_PROCEDURE_DATA_FRAGMENT}
  query listProcedureGroupsByDepartment(
    $departmentId: String!
    $limit: Int = 100
    $nextToken: String
    $procedureFilter: ListProceduresFilter
  ) {
    listProcedureGroupsByDepartmentv2(
      input: {
        departmentId: $departmentId
        filter: $procedureFilter
        paginationInput: { limit: $limit, nextToken: $nextToken }
      }
    ) {
      items {
        draftProcedure {
          ...procedureData
        }
        publishedProcedure {
          ...procedureData
        }
      }
      nextToken
    }
  }
`;

export const LIST_ALL_PROCEDURE_GROUPS_BY_DEPARTMENT = gql`
  ${PROCEDURE_SUMMARY_FRAGMENT}
  query listAllProcedureGroupsByDepartment(
    $departmentId: ID!
    $limit: Int = 150
  ) {
    listProcedureGroupsByDepartment(
      departmentId: $departmentId
      limit: $limit
    ) {
      items {
        draftProcedure {
          ...procedureSummary
        }
        publishedProcedure {
          ...procedureSummary
        }
      }
    }
  }
`;

export const GET_PROCESS_VISUALIZATION_URL = gql`
  query GetProcedureVisualizationUrl(
    $procedureId: ID!
    $departmentId: ID!
    $onlyIfExists: Boolean = false
    $expiresIn: Int = 300
    $procedureStage: ProcedureStage = null
  ) {
    getProcedureVisualizationUrl(
      procedureId: $procedureId
      departmentId: $departmentId
      onlyIfExists: $onlyIfExists
      expiresIn: $expiresIn
      procedureStage: $procedureStage
    ) {
      s3Url
      presignedUrl
    }
  }
`;
